import { UserProfileForm } from '@grcs/types/UserRole';

export const generateStudentUsername = (firstName: string, email: string) =>
  `${firstName.toLowerCase().replace(/\s+/g, '')}_${email
    .toLowerCase()
    .replace(/\s+/g, '')}`;

/** Shared by form validation, CSV preview, lookup, and the final write path. */
export const validateStudentInput = (input: UserProfileForm) => {
  const text = (value: unknown) =>
    typeof value === 'string' ? value.trim() : '';
  const data: UserProfileForm = {
    first_name: text(input.first_name),
    last_name: text(input.last_name),
    email: text(input.email).toLowerCase(),
    dob: text(input.dob),
  };
  const errors: Partial<Record<keyof UserProfileForm, string>> = {};
  if (!data.first_name) errors.first_name = 'First name is required';
  if (!data.last_name) errors.last_name = 'Last name is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
    errors.email = 'Enter a valid email address';
  const match = data.dob.match(/^(\d{4})([-/])(\d{1,2})\2(\d{1,2})$/);
  const canonical = match
    ? `${match[1]}-${match[3].padStart(2, '0')}-${match[4].padStart(2, '0')}`
    : '';
  const date = new Date(`${canonical}T00:00:00Z`);
  if (
    !match ||
    Number(match[1]) < 1 ||
    date.getUTCFullYear() !== Number(match[1]) ||
    date.getUTCMonth() + 1 !== Number(match[3]) ||
    date.getUTCDate() !== Number(match[4])
  ) {
    errors.dob = 'Enter a valid date of birth (YYYY-MM-DD or YYYY/MM/DD)';
  } else data.dob = canonical;
  return { data, errors };
};

export const normalizeStudentInput = (input: UserProfileForm) => {
  const { data, errors } = validateStudentInput(input);
  if (Object.keys(errors).length)
    throw new Error(Object.values(errors).join('. '));
  return data;
};

export const validateStudentCSV = (rows: any[]) => {
  const errors: string[] = [];
  const identities = new Map<string, number>();
  if (!rows.length) errors.push('CSV file is empty');
  rows.forEach((row, index) => {
    const result = validateStudentInput(row);
    Object.values(result.errors).forEach((message) =>
      errors.push(`Row ${index + 1}: ${message}`),
    );
    if (typeof row.class !== 'string' || !row.class.trim())
      errors.push(`Row ${index + 1}: Class is required`);
    if (!Object.keys(result.errors).length) {
      const username = generateStudentUsername(
        result.data.first_name,
        result.data.email,
      );
      const previous = identities.get(username);
      if (previous !== undefined)
        errors.push(
          `Row ${
            index + 1
          }: Duplicate student from row ${previous}; include each student only once, with one class.`,
        );
      else identities.set(username, index + 1);
    }
  });
  return { isValid: !errors.length, errors };
};
